import React, { useContext, useEffect, useMemo, useState } from 'react';
import CardBox from '@/pages/dashboard/components/CardBox';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { addChartLabel, compareTrend, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { useLocal, useOption, useWeekDayArr } from '@/pages/dashboard/utils/customHook';
import { commonValueNoThousandFormatter, formatPercentNum, getWeekDayTitle, handleDownload } from '@/pages/dashboard/utils/utils';
import { defaultSetOtherConfigFunc, getOptions, getRatioText } from '../../_config/utils';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import TitleDes from '@/pages/dashboard/components/TitleDes';
import useTitleDes from '@/pages/dashboard/hooks/useTitleDes';
import { connect } from 'umi';
import moment from 'moment';

/**
 * 活跃用户分析类型枚举
 * 
 * 定义了 LoginActiveUsersCo 组件支持的不同分析模式，
 * 每种类型对应不同的数据处理逻辑、本地化命名空间和图表配置。
 * 
 * @enum {string}
 */
enum ActiveUserType {
  ActiveUserTrendAnalysis = 'activeUserTrendAnalysis',
  WeeklyActiveUserTrendAnalysis = 'weeklyActiveUserTrendAnalysis',
  AvgWeeklyActiveUsersActivity = 'avgWeeklyActiveUsersActivity',
  MonthlyActiveUsersActivity = 'monthlyActiveUsersActivity',
};

/**
 * ActiveUserTrendAnalysis 接口返回的数据结构 
 */
interface IActiveUserTrendAnalysis {
  date: string;
  active_category: string;
  value: string;
  dod: string;
};

/**
 * ActiveUserTrendAnalysis 格式化后的目标数据结构
 */
interface IActiveUserTrendAnalysisFormatted {
  date: string;
  high_active_value: string;
  high_active_dod: string;
  medium_active_value: string;
  medium_active_dod: string;
  low_active_value: string;
  low_active_dod: string;
};

/**
 * AvgWeeklyActiveUsersActivity 和 MonthlyActiveUsersActivity 接口返回的数据结构
 */
interface IActiveUsersActivity {
  day: string;
  this_value: string | number;
  last_value: string | number;
};

function LoginActiveUsersCo(props: any) {
  const { data: comData, config: comConfig, explainInfo } = props || {};
  const contextObj = useContext(FilterContext);
  const { filterVal = {} } = contextObj || {};
  const { timeType, rangeTime } = filterVal || {};
  const isWeek = timeType === 'Week';
  const {
    // type 用于区分不同的卡片类型，比如：活跃用户趋势分析、活跃用户周趋势分析、用户周均活跃登录行为等
    type: comConfigType = ActiveUserType.AvgWeeklyActiveUsersActivity,
    title,
    titleCN,
    titleDes: titleDesConfig,
    defaultAvtive: defaultActive = 1,
    resKey = 'active_user_activity',
    componentMetrics,
    isHaveSubTitle,
    renderListOptions = {},
  }: { type?: ActiveUserType | string } & typeof comConfig = comConfig || {};

  // 将外部传入的 type 转换为枚举类型
  const type: ActiveUserType | string =
    typeof comConfigType === 'string'
      ? ActiveUserType[comConfigType as keyof typeof ActiveUserType] ?? comConfigType
      : comConfigType;

  const isActivity = type === ActiveUserType.AvgWeeklyActiveUsersActivity || type === ActiveUserType.MonthlyActiveUsersActivity;
  const isTrend = type === ActiveUserType.ActiveUserTrendAnalysis || type === ActiveUserType.WeeklyActiveUserTrendAnalysis;

  // 根据 type 准备命名空间，用于获取本地化文本
  const namespace = (() => {
    if (type === ActiveUserType.ActiveUserTrendAnalysis)
      return 'engagement_active_degree_active_user_trend_analysis';
  
    if (type === ActiveUserType.WeeklyActiveUserTrendAnalysis)
      return 'engagement_active_degree_weekly_active_user_trend_analysis';

    // 向后兼容
    return isWeek ? 'engagement_active_degree_weekly' : 'engagement_active_degree_monthly';
  })();

  const isSingleWeek = (rangeTime?: any[]) => {
    if (!rangeTime || rangeTime?.length !== 2) return undefined;

    const startDate = moment(rangeTime[0]);
    const endDate = moment(rangeTime[1]);

    return startDate.isSame(endDate, 'week');
  };

  const { locale, getLocalText, formatMessage, isEN } = useLocal(namespace);
  const { data: originData, loading, errorMsg: error } = comData?.weekData || {};
  const [data, setData] = useState({
    [resKey]: [],
    time_range: '',
  });
  const [isEye, setIsEye] = useState(true);
  const weekDayArr = useWeekDayArr();
  const subTitleTime = timeType === 'Week' ? getWeekDayTitle(data?.time_range, weekDayArr, '~') : momentFormat(rangeTime?.[1], 'YYYY-MM');

  const getConfig = (origin: any[], module: string) => {
    const formatterFunc = (params: any) => {
      let data: any[] = [];
      let title = '';

      if (Array.isArray(params)) {
        data = params?.map((item, index) => {
          const isSecond = index === 1;
          const nameId = isActivity
            ? `${module}_legend${
                isSecond && showLastWeekActivity === false ? 3 : index + 1
              }`
            : `${module}hover${index + 1}`;

          // 浮窗内标题
          title = isActivity
            ? getLocalText(`${module}_chart_title`, { day: item?.name })
            : item?.name;
          // 浮窗内 series 显示的名称
          const name = getLocalText(nameId);
          // 浮窗内 series 名称右边的数值
          const metricsNum = item?.value
            ? dataFormat({ value: item?.value, formatRule: 'thousandsInit' })
            : '-';
          const target = origin?.find((originItem) =>
            isActivity
              ? originItem.day === item.axisValue
              : originItem.date === item.axisValue,
          );
          const ratioNum = isActivity
            ? target?.[index === 0 ? 'this_change' : 'last_change']
            : target?.[
                index === 0
                  ? 'high_active_dod'
                  : index === 1
                  ? 'medium_active_dod'
                  : 'low_active_dod'
              ];

          // 如果当前是第二项，并且不是单周，则不显示比例
          const showDod = !(isSecond && showLastWeekActivity === false);

          const { text, status } = getRatioText(
            ratioNum,
            componentMetrics?.chartPercentDecimal,
            true,
          );
          return {
            name,
            metricsNum,
            ratioNum: showDod ? text : '',
            ratioStatus: showDod ? status : '',
            color: item.color,
          };
        });
      } else {
        // todo 能否配置化处理
        title = getLocalText(`${module}_chart_title`, { day: params?.name });
        data = [
          {
            name: params?.name,
            metricsNum: dataFormat({ value: params?.value, formatRule: 'thousandsInit'}),
            ratioNum: formatPercentNum(+origin?.[params.dataIndex]?.ratio, componentMetrics?.chartPercentDecimal ? componentMetrics?.chartPercentDecimal : 1),
            color: params.color,
          },
        ];
      }

      return getEchartTooltipText({
        title,
        metricsName: '',
        ratioName: '',
        hasRatio: true,
        data,
      });
    };

    const additionalSetOtherConfigFunc = (opt: any) => {
      if (isActivity) {
        opt.legend.formatter = (name: string) => {
          const index = opt?.legend?.data?.findIndex((item: any) => item === name) + 1;
          const id = showLastWeekActivity === false && index === 2 ? `${module}_legend3` : `${module}_legend${index}`;
          return getLocalText(id);
        }
      }
    };

    return {
      bar: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          defaultSetOtherConfigFunc({ opt, module, getLocalText });
          additionalSetOtherConfigFunc(opt);
          return opt;
        }
      },
      line: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          defaultSetOtherConfigFunc({ opt, module, getLocalText });
          additionalSetOtherConfigFunc(opt);
          return opt;
        }
      },
      pie: {
        formatterFunc,
        setOtherConfigFunc: (opt: any) => {
          defaultSetOtherConfigFunc({ opt, module, getLocalText });
          additionalSetOtherConfigFunc(opt);
          return opt;
        }
      },
    };
  };

  // 为 true 时显示上一周的活跃用户，为 false 时显示平均值，仅适用于 AvgWeeklyActiveUsersActivity
  const showLastWeekActivity = useMemo(() => {
    if (timeType?.toLowerCase() !== 'week' || !rangeTime || rangeTime?.length !== 2 || type !== ActiveUserType.AvgWeeklyActiveUsersActivity) {
      return undefined;
    }

    return isSingleWeek(rangeTime);
  }, [timeType, rangeTime]);

  const { option, comTitle, config } = useMemo(() => {
    const trendWeekMap = [
      {
        name: formatMessage({id: 'engagement_active_degree_active_user_trend_analysis_hover1'}),
        key: 'high_active_value',
      },
      {
        name: formatMessage({id: 'engagement_active_degree_active_user_trend_analysis_hover2'}),
        key: 'medium_active_value',
      },
      {
        name: formatMessage({id: 'engagement_active_degree_active_user_trend_analysis_hover3'}),
        key: 'low_active_value',
      },
    ];

    const activityWeekMap = [
      {
        name: 'This Week',
        key: 'this_value',
      },
      {
        name: 'Last Week',
        key: 'last_value',
      },
    ];

    const optionsMapObj: any = {
      Week: {
        // X 轴读取的 key
        x: isActivity ? 'day' : 'date',
        // legend 的配置
        legend: isActivity ? activityWeekMap : trendWeekMap,
      },
      Month: {
        x: 'day',
        legend: [
          {
            name: 'This Month',
            key: 'this_value',
          },
          {
            name: 'Last Month',
            key: 'last_value',
          },
        ],
      },
    };

    const trendWeekTable = [
      {
        key: 'date',
        name: 'Date',
        header: formatMessage({id: 'engagement_active_degree_active_user_trend_analysis_date'}),
      },
      {
        key: 'high_active_value',
        name: 'High Active',
        header: formatMessage({id: 'engagement_active_degree_active_user_trend_analysis_hover1'}),
        render: (record: { high_active_value: any; }) => dataFormat({ value: record.high_active_value, formatRule: 'thousandsInit'}) 
      },
      {
        key: 'medium_active_value',
        name: 'Medium Active',
        header: formatMessage({id: 'engagement_active_degree_active_user_trend_analysis_hover2'}),
        render: (record: { medium_active_value: any; }) => dataFormat({ value: record.medium_active_value, formatRule: 'thousandsInit'}) 
      },
      {
        key: 'low_active_value',
        name: 'Low Active',
        header: formatMessage({id: 'engagement_active_degree_active_user_trend_analysis_hover3'}),
        render: (record: { low_active_value: any; }) => dataFormat({ value: record.low_active_value, formatRule: 'thousandsInit'}) 
      },
    ];

    const activityWeekTable = [
      {
        key: 'day',
        name: 'Day',
        header: formatMessage({id: 'engagement_active_degree_weekly_activity_date'}),
        render: (record: { day: string; }) => record.day?.replace('day', ' day')
      },
      {
        key: 'this_value',
        name: 'This Week',
        header: formatMessage({id: 'engagement_active_degree_weekly_activity_legend1'}),
        render: (record: { this_value: any; }) => dataFormat({ value: record.this_value, formatRule: 'thousandsInit'}) 
      },
      {
        key: 'last_value',
        name: 'Last Week',
        header: formatMessage({id: showLastWeekActivity ? 'engagement_active_degree_weekly_activity_legend2' : 'engagement_active_degree_weekly_activity_legend3'}),
        render: (record: { last_value: any; }) => dataFormat({ value: record.last_value, formatRule: 'thousandsInit'})
      },
    ];

    const optionsTableObj: any = {
      Week: isActivity ? activityWeekTable : trendWeekTable,
      Month: [
        {
          key: 'day',
          name: 'Day',
          header: formatMessage({id: 'engagement_active_degree_monthly_date'})
        },
        {
          key: 'this_value',
          name: 'This Month',
          header: formatMessage({id: 'engagement_active_degree_monthly_activity_legend1'}),
          render: (record: { this_value: any; }) => dataFormat({ value: record.this_value, formatRule: 'thousandsInit'}) 
        },
        {
          key: 'last_value',
          name: 'Last Month',
          header: formatMessage({id: 'engagement_active_degree_monthly_activity_legend2'}),
          render: (record: { last_value: any; }) => dataFormat({ value: record.last_value, formatRule: 'thousandsInit'})
        },
      ],
    };

    const option = timeType && getOptions(
      optionsMapObj?.[timeType],
      optionsTableObj?.[timeType],
      data?.[resKey],
    );

    // 将 table 中第一列（即 Date 列）设置为左对齐
    if (isTrend && option?.table?.option) {
      const firstCol = option.table.option[0];
      firstCol.align = 'left';
    }

    let module = '';
    if (type === ActiveUserType.AvgWeeklyActiveUsersActivity || type === ActiveUserType.MonthlyActiveUsersActivity) {
      module = 'activity';
    }

    const comTitle = isEN ? title : titleCN;
    const config = getConfig(data?.[resKey], module);

    return {
      option,
      comTitle,
      config,
    };
  }, [data, comConfig, isEN]);

  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(option);

  useEffect(() => {
    if (!originData) return;

    const tempData = {
      time_range: originData?.time_range,
    };

    // 处理 AvgWeeklyActiveUsersActivity 和 MonthlyActiveUsersActivity 数据
    if (isActivity) {
      setData({
        [resKey]: originData[resKey]?.map?.((item: IActiveUsersActivity) => {
          return {
            ...item,
            day: formatMessage({id: 'engagement_active_degree_weekly_activity_day'}, { day: item.day, unit: Number(item.day) > 1 ? 'days' : 'day' }),
            this_value: commonValueNoThousandFormatter(componentMetrics?.type, item.this_value, componentMetrics?.decimal),
            last_value: commonValueNoThousandFormatter(componentMetrics?.type, item.last_value, componentMetrics?.decimal),
          };
        }),
        ...tempData,
      });
    }
    // 处理 ActiveUserTrendAnalysis 数据
    else if (isTrend) {
      if (!originData[resKey] || !Array.isArray(originData[resKey])) return;

      const formatted: IActiveUserTrendAnalysisFormatted[] = Object.values(
        originData[resKey].reduce((acc, item: IActiveUserTrendAnalysis) => {
          if (!acc[item.date]) {
            acc[item.date] = { date: item.date };

            const year = item.date.slice(0, 4);
            const month = item.date.slice(4, 6);
            const day = item.date.slice(6, 8);
            acc[item.date] = { date: `${year}-${month}-${day}` };
          }
          const prefix = item.active_category.toLowerCase() + "_active";
          acc[item.date][`${prefix}_value`] = item.value;
          acc[item.date][`${prefix}_dod`] = item.dod;
          return acc;
        }, {})
      );

      setData({
        [resKey]: formatted?.map?.((item: IActiveUserTrendAnalysisFormatted) => {
          return {
            ...item,
            high_active_value: commonValueNoThousandFormatter(componentMetrics?.type, item.high_active_value, componentMetrics?.decimal),
            medium_active_value: commonValueNoThousandFormatter(componentMetrics?.type, item.medium_active_value, componentMetrics?.decimal),
            low_active_value: commonValueNoThousandFormatter(componentMetrics?.type, item.low_active_value, componentMetrics?.decimal),
          };
        }),
        ...tempData,
      });
    }
  }, [originData, locale]);

  const renderOption = (key: string, obj: any) =>
    renderListOptions?.[key] !== false ? [obj] : [];

  const renderList = [
    ...(renderOption('eyes', {
      type: 'eyes',
      svgType: isEye ? 'open-eyes' : 'eyes-closed',
      content: isEye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    })),
    ...(renderOption('barChart', {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        height={340}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? barSeries : []}
        formatterFunc={config?.bar?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (config?.bar?.setOtherConfigFunc) opt = config?.bar?.setOtherConfigFunc(opt);
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => (params.value)
          });
          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; }) => {
              const result = dataFormat({ value: params?.value, formatRule: componentMetrics?.chartFormatRule || 'thousandsInit'});
              return result;
            },
            show: isEye,
            formatNum: (value: string | number) => value
          });

          return opt;
        }}
      />
    })),
    ...(renderOption('lineChart', {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        height={340}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? lineSeries : []}
        formatterFunc={config?.line?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt);
          opt = compareTrend(opt);
          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; }) => {
              const result = dataFormat({ value: params?.value, formatRule: componentMetrics?.chartFormatRule || 'thousandsInit'});
              return result;
            },
            show: isEye,
            formatNum: (value: string | number) => value
          });
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => (params.value)
          });

          if (isTrend) {
            opt.color = ['#5086F3', '#F8D74D', '#78E198'];
            opt.series.forEach((series: any, index: number) => {
              series.lineStyle = {
                type: 'solid',
              };

              series.itemStyle = {
                normal: {
                  lineStyle: {
                    width: 2,
                    type: 'solid',
                  },
                },
              };
            });
          }

          return opt;
        }}
      />
    })),
    ...(renderOption('sheet', {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={340}
        loading={false}
        tableOption={option?.table?.option}
        tableList={option?.table?.data}
      />
    })),
  ];

  // 使用封装的useTitleDes Hook
  const titleDes = useTitleDes({
    titleDesConfig,
    explainInfo,
    isEN,
  });

  return (
    <>
      <CardBox
        className='register-table'
        height={340}
        bordered={false}
        titleCom={
          <>
            {comTitle}
            <TitleDes titleDes={titleDes} />
          </>
        }
        subTitleCom={isHaveSubTitle ? <div className='register-table-time'>{subTitleTime}</div> : ''}
        renderList={renderList}
        defaultAvtive={defaultActive}
        setCurrentDataType={(type: any) => {
          if (type === 'eyes') {
            setIsEye(!isEye);
          }
        }}
        downloadClick={async () => {
          handleDownload(option?.table, comTitle, false);
        }}
        isLoading={loading}
        isError={!!error}
        isNodata={isNodata}
      />
    </>
  );
}

export default connect((state: any) => ({
  explainInfo: state.dashboard?.explainInfo,
}))(LoginActiveUsersCo);
